import { Inject, Injectable } from '@angular/core';
import { UserData } from '@app/shared/models/user-data.model';
import { AuthService } from "@services/auth/auth.service";
import { catchError, Observable, of, shareReplay } from 'rxjs';
import { HttpClient } from "@angular/common/http";
import { map } from "rxjs/operators";
import { BASE_AUTH_URL } from "@app/core/core.module";
import { Tokens } from "@shared/models/tokens.model";

@Injectable({
  providedIn: 'root'
})
export class AuthApisixService extends AuthService {

  private isAuthenticated?: Observable<boolean>;
  private userDataObservable?: Observable<UserData>;
  private tokensObservable?: Observable<Tokens>;

  constructor(@Inject(BASE_AUTH_URL) private baseUrl: string, private http: HttpClient) {
    super();
  }

  public override login(): void {
    location.href = this.baseUrl + '/login?redirect_uri=' + encodeURIComponent(window.location.origin) + '/ask-gene';
  }

  public override logout(): void {
    location.href = this.baseUrl + '/logout';
  }

  public override get isAuthenticated$(): Observable<boolean> {
    if (!this.isAuthenticated) {
      this.isAuthenticated = this.http.request('GET', this.baseUrl + '/', {
        responseType: 'text',
      }).pipe(
        map(() => {
          return true;
        }),
        catchError(() => {
          return of(false)
        }),
        shareReplay()
      );
    }
    return this.isAuthenticated;
  }

  public override get userData$(): Observable<UserData> {
    if (!this.userDataObservable) {
      this.userDataObservable = this.http.request('GET', this.baseUrl + '/api/v1/tokens/claims').pipe(
        map(response => {
          return this.mapUserData(response);
        }),
        shareReplay()
      );
    }
    return this.userDataObservable;
  }

  public override getAccessToken(): Observable<string> {
    return this.getTokens().pipe(
      map(response => {
        return response.access_token;
      }),
    );
  }

  public override getIdToken(): Observable<string> {
    return this.getTokens().pipe(
      map(response => {
        return response.id_token;
      }),
    );
  }

  public override getRefreshToken(): Observable<string> {
    return this.getTokens().pipe(
      map(response => {
        return response.refresh_token;
      }),
    );
  }

  public override getMSGraphAccessToken(): Observable<string> {
    return this.http.request<any>('GET', this.baseUrl + '/api/v1/tokens/ms-graph').pipe(
      map(response => {
        return response.access_token;
      })
    );
  }

  public override checkAuth(): Observable<{ isAuthenticated: boolean; userData: UserData | undefined; }> {
    return this.http.request('GET', this.baseUrl + '/api/v1/tokens/claims').pipe(
      map(response => {
        let userData = this.mapUserData(response)
        return {isAuthenticated: true, userData: userData};
      }),
      catchError(() => of({isAuthenticated: false, userData: undefined}))
    );
  }

  private getTokens(): Observable<Tokens> {
    if (!this.tokensObservable) {
      this.tokensObservable =  this.http.request<Tokens>('GET', this.baseUrl + '/api/v1/tokens', {
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        }
      }).pipe(
        shareReplay()
      )
    }
    return this.tokensObservable;
  }

  private mapUserData(userData: any): UserData {
    return new UserData(
      userData.oid,
      userData.tid,
      userData.name,
      userData.unique_name,
      userData.roles
    );
  }

}
